import React, { useEffect, useRef, useState } from "react";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { Play, Volume2, VolumeX, Loader2 } from "lucide-react";

/**
 * Adds a media-fragment hint to the video URL so browsers render a frame
 * (at t=0.1s) as an implicit poster while `preload="metadata"` finishes.
 * Falls back gracefully when the URL already contains a query string.
 */
function withFragmentHint(url) {
  if (!url) return url;
  // Skip only when a time-fragment is already present; other anchors are fine.
  return /#t=/.test(url) ? url : `${url}#t=0.1`;
}

function ReelCard({ reel }) {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [muted, setMuted] = useState(true);
  const [posterReady, setPosterReady] = useState(false);
  const [inView, setInView] = useState(false);
  const isVideo = /\.(mp4|webm|mov|m4v)(\?|$|#)/i.test(reel.video_url || "");

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) { v.pause(); return; }
    // On tap-to-play, bump preload so the browser aggressively fetches.
    // (Only meaningful for the FIRST play — subsequent plays are already buffered.)
    if (v.preload !== "auto") v.preload = "auto";
    setBuffering(true);
    v.play().catch(() => setBuffering(false));
  };

  // Only start loading the <video> once it scrolls into (or near) the viewport.
  // For a 170-reel grid this prevents 170 concurrent range requests to S3 and
  // dramatically speeds up first-poster rendering.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } });
      },
      { rootMargin: "600px 0px" },  // pre-load ~600px before visible
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => { setPlaying(true); setBuffering(false); };
    const onPause = () => setPlaying(false);
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);
    const onFrameReady = () => setPosterReady(true);
    // Safari mobile is aggressive about deferring beyond `loadedmetadata` when
    // preload='metadata' — we treat metadata-ready as poster-ready so users see
    // the tile faster. If the browser later gets a real frame, we still hide
    // the spinner via loadeddata/canplay.
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("loadedmetadata", onFrameReady);
    v.addEventListener("loadeddata", onFrameReady);
    v.addEventListener("canplay", onFrameReady);
    // Also nudge the video: seek explicitly to 0.1s so decoders that ignore the
    // media-fragment URL hint still fetch and paint a real frame.
    const seek = () => { try { if (v.readyState >= 1 && v.currentTime === 0) v.currentTime = 0.1; } catch { /* seek may throw before source ready */ } };
    v.addEventListener("loadedmetadata", seek);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("loadedmetadata", onFrameReady);
      v.removeEventListener("loadeddata", onFrameReady);
      v.removeEventListener("canplay", onFrameReady);
      v.removeEventListener("loadedmetadata", seek);
    };
  }, [inView]);

  const w = reel.video?.width || 0;
  const h = reel.video?.height || 0;
  const ratio = (w > 0 && h > 0) ? `${w} / ${h}` : "9 / 16";
  const bg = reel.video?.dominant_color || "#111";

  return (
    <div
      ref={wrapRef}
      data-testid={`reel-${reel.id}`}
      className="relative snap-start w-full max-w-[420px] mx-auto rounded-none md:rounded-xl overflow-hidden"
      style={{ aspectRatio: ratio, maxHeight: "85vh", backgroundColor: bg }}
    >
      {isVideo ? (
        <>
          {inView && (
            <video
              ref={videoRef}
              src={withFragmentHint(reel.video_url)}
              {...(reel.thumbnail_url ? { poster: reel.thumbnail_url } : {})}
              className="w-full h-full object-contain bg-black transition-opacity duration-300"
              style={{ opacity: posterReady || reel.thumbnail_url ? 1 : 0 }}
              playsInline
              loop
              muted={muted}
              preload="metadata"
            />
          )}
          {(!inView || !posterReady) && !reel.thumbnail_url && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 size={24} className="animate-spin text-white/40" />
            </div>
          )}
        </>
      ) : (reel.thumbnail_url || reel.video_url) ? (
        <img src={reel.thumbnail_url || reel.video_url} alt={reel.title} className="w-full h-full object-contain bg-black" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[color:var(--text-muted)] text-xs eyebrow">No preview</div>
      )}

      {/* Play overlay */}
      <button onClick={toggle} className="absolute inset-0 flex items-center justify-center group" aria-label={playing ? "Pause" : "Play"} data-testid={`reel-play-${reel.id}`}>
        {buffering ? (
          <div className="w-16 h-16 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center">
            <Loader2 size={26} className="animate-spin text-white" />
          </div>
        ) : !playing ? (
          <div className="w-16 h-16 rounded-full bg-[color:var(--text)]/90 flex items-center justify-center group-hover:scale-110 transition-transform">
            <Play size={22} className="text-[color:var(--bg)] ml-1" fill="currentColor" />
          </div>
        ) : null}
      </button>

      {isVideo && (
        <button
          data-testid={`reel-mute-${reel.id}`}
          onClick={() => setMuted((m) => !m)}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center"
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
      )}

      <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/90 to-transparent">
        <div className="font-display italic text-2xl leading-tight">{reel.title}</div>
        {reel.caption && <div className="mt-1 text-sm text-[color:var(--text-muted)] line-clamp-2">{reel.caption}</div>}
      </div>
    </div>
  );
}

export default function Reels() {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/reels")
      .then((r) => setReels(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <PublicLayout>
      <section className="pt-32 md:pt-40 pb-8">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <div className="eyebrow mb-4" data-testid="reels-count-eyebrow">
            Motion · {loading ? "…" : reels.length} Reels
          </div>
          <h1 className="font-display italic text-5xl md:text-8xl leading-[0.95] tracking-tight max-w-3xl">
            Reels, films, and passing moments.
          </h1>
        </div>
      </section>

      <section data-testid="reels-list" className="py-8 md:py-16">
        {loading ? (
          <div className="text-center py-24 text-[color:var(--text-muted)]" data-testid="reels-loading">
            <Loader2 size={20} className="inline animate-spin mr-2" />
            Loading reels…
          </div>
        ) : reels.length === 0 ? (
          <div className="text-center py-24 text-[color:var(--text-muted)]" data-testid="reels-empty">
            No reels yet.
          </div>
        ) : (
          <div className="flex flex-col md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-[1500px] mx-auto px-0 md:px-10 items-center md:items-start justify-items-center">
            {reels.map((r) => (
              <ReelCard key={r.id} reel={r} />
            ))}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
